import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ShareCollection from "@/components/ShareCollection";
import { GROUP_LABELS } from "@/components/CatalogFilters";
import { Search, SlidersHorizontal, Sparkles, User, Users, Baby, LayoutGrid, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import HeroBanner from "@/components/HeroBanner";
import ProductCard from "@/components/ProductCard";
import BuyerApprovalBanner from "@/components/BuyerApprovalBanner";
import CategoryTiles from "@/components/CategoryTiles";
import CatalogFilters from "@/components/CatalogFilters";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";

const PAGE = 24;
const GENDERS = [
  { id: "All", label: "All", icon: LayoutGrid },
  { id: "Mens", label: "Mens", icon: User },
  { id: "Womens", label: "Womens", icon: Users },
  { id: "Kids", label: "Kids", icon: Baby },
];
const INITIAL = { gender: "All", category: "All", brand: "All", size: "All", colour: "All", priceMin: "", priceMax: "", sort: "newest", newOnly: false, group: "" };

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const squash = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const groupFromSlug = (s) => {
  const n = squash(s);
  if (!n) return "";
  return Object.keys(GROUP_LABELS).find((g) => g === n || squash(GROUP_LABELS[g]) === n || squash(GROUP_LABELS[g]).startsWith(n)) || "";
};

function filtersFromUrl(sp) {
  const g = sp.get("gender");
  const cat = sp.get("category") || "";
  const group = sp.get("group") || "";
  return {
    ...INITIAL,
    gender: g ? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase() : "All",
    category: cat || "All",
    group,
    brand: sp.get("brand") || "All",
    size: (sp.get("size") || "All").toUpperCase() === "ALL" ? "All" : sp.get("size").toUpperCase(),
    colour: sp.get("colour") || "All",
    sort: sp.get("sort") || "newest",
    newOnly: sp.get("new") === "1",
    priceMin: sp.get("min") || "",
    priceMax: sp.get("max") || "",
  };
}

function filtersToUrl(f, q) {
  const p = {};
  if (f.gender !== "All") p.gender = f.gender.toLowerCase();
  if (f.group) p.category = slug(GROUP_LABELS[f.group] || f.group);
  else if (f.category !== "All") p.category = slug(f.category);
  if (f.brand !== "All") p.brand = slug(f.brand);
  if (f.size !== "All") p.size = f.size.toLowerCase();
  if (f.colour !== "All") p.colour = slug(f.colour);
  if (f.sort !== "newest") p.sort = f.sort;
  if (f.newOnly) p.new = "1";
  if (f.priceMin) p.min = f.priceMin;
  if (f.priceMax) p.max = f.priceMax;
  if (q.trim()) p.q = q.trim();
  return p;
}

function Breadcrumbs({ f, onHome }) {
  const parts = [f.gender !== "All" && f.gender, f.group ? GROUP_LABELS[f.group] : f.category !== "All" && f.category, f.brand !== "All" && f.brand].filter(Boolean);
  if (!parts.length) return null;
  return (
    <nav className="text-sm text-slate-500 mb-3 flex flex-wrap items-center gap-1.5" aria-label="Breadcrumb" data-testid="catalogue-breadcrumbs">
      <button onClick={onHome} className="hover:text-slate-900 underline-offset-2 hover:underline" data-testid="breadcrumb-home">Catalogue</button>
      {parts.map((p) => (<React.Fragment key={p}><span className="text-slate-300">/</span><span className="font-semibold text-slate-800">{p}</span></React.Fragment>))}
    </nav>
  );
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function buildParams(f, q, skip) {
  const p = { skip, limit: PAGE, sort: f.sort };
  if (q.trim()) p.search = q.trim();
  ["gender", "category", "brand", "size", "colour"].forEach((k) => { if (f[k] !== "All") p[k] = f[k]; });
  if (f.priceMin) p.price_min = f.priceMin;
  if (f.priceMax) p.price_max = f.priceMax;
  if (f.newOnly) p.new = true;
  if (f.group) p.group = f.group;
  return p;
}

function useCatalog(filters, query, user) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const reqId = useRef(0);
  const key = JSON.stringify([filters, query, user?.id || null]);

  const fetchPage = useCallback(async (skip) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: buildParams(filters, query, skip) });
      if (id !== reqId.current) return;
      setItems((prev) => (skip === 0 ? data.products : [...prev, ...data.products]));
      setTotal(data.total);
      setHasMore(data.has_more);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    setItems([]);
    fetchPage(0);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) fetchPage(items.length);
  }, [loading, hasMore, items.length, fetchPage]);

  return { items, total, hasMore, loading, loadMore };
}

function InfiniteSentinel({ onVisible, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (disabled || !ref.current) return undefined;
    const ob = new IntersectionObserver((e) => { if (e[0].isIntersecting) onVisible(); }, { rootMargin: "600px" });
    ob.observe(ref.current);
    return () => ob.disconnect();
  }, [onVisible, disabled]);
  return <div ref={ref} className="h-1" data-testid="infinite-sentinel" />;
}

function GenderStrip({ value, onChange, counts }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="gender-strip">
      {GENDERS.map((g) => {
        const Icon = g.icon;
        const n = g.id === "All" ? null : counts?.[g.id];
        return (
          <button key={g.id} onClick={() => onChange(g.id)} data-testid={`gender-${g.id}`}
            className={`shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
              value === g.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-slate-400"}`}>
            <Icon className="h-4 w-4" /> {g.label}
            {n != null && <span className="text-xs font-normal opacity-60">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

function NewArrivalsStrip({ items, total, onViewAll }) {
  if (!items.length) return null;
  return (
    <section className="mb-10" data-testid="new-arrivals-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 inline-flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#E6C200]" /> New Arrivals
          <span className="text-xs sm:text-sm font-normal text-slate-400">last 14 days · {total}</span>
        </h3>
        <button onClick={onViewAll} className="text-sm font-semibold text-slate-900 hover:underline" data-testid="view-all-new-arrivals">View all →</button>
      </div>
      <div className="flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 snap-x" data-testid="new-arrivals-carousel">
        {items.map((p) => (
          <div key={p.id} className="w-[46%] sm:w-[31%] lg:w-[23%] shrink-0 snap-start"><ProductCard product={p} idPrefix="new-" /></div>
        ))}
      </div>
    </section>
  );
}

export default function Catalogue() {
  const { user, approved } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const f = filtersFromUrl(sp);
    const g = !f.group && f.category !== "All" ? groupFromSlug(f.category) : "";
    return g ? { ...f, group: g, category: "All" } : f;
  });
  const [search, setSearch] = useState(sp.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);
  const [facets, setFacets] = useState(null);
  const [newArrivals, setNewArrivals] = useState({ products: [], total: 0 });
  const q = useDebounced(search, 300);
  const debFilters = useDebounced(filters, 250);
  const { items, total, hasMore, loading, loadMore } = useCatalog(debFilters, q, user);
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearAll = () => { setFilters(INITIAL); setSearch(""); };

  useEffect(() => {
    if ([...sp.keys()].length) {
      setTimeout(() => document.getElementById("catalogue")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSp(filtersToUrl(filters, q), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, q]);

  useEffect(() => {
    if (!facets) return;
    const match = (val, list) => {
      if (!val || val === "All") return val;
      const hit = (list || []).find((x) => x.name === val || squash(x.name) === squash(val))
        || [...(list || [])].sort((a, b) => a.name.length - b.name.length).find((x) => squash(val) && squash(x.name).startsWith(squash(val)));
      return hit ? hit.name : val;
    };
    setFilters((f) => {
      const next = { ...f, brand: match(f.brand, facets.brands), category: match(f.category, facets.categories), colour: match(f.colour, facets.colours) };
      return JSON.stringify(next) === JSON.stringify(f) ? f : next;
    });
  }, [facets]);

  useEffect(() => {
    api.get("/catalog/facets", { params: filters.gender !== "All" ? { gender: filters.gender } : {} })
      .then((r) => setFacets(r.data)).catch(() => {});
  }, [filters.gender, user]);

  useEffect(() => {
    api.get("/products/new-arrivals", { params: { limit: 8 } }).then((r) => setNewArrivals(r.data)).catch(() => {});
  }, [user]);

  const pristine = JSON.stringify(filters) === JSON.stringify(INITIAL) && !search.trim();

  return (
    <div className="App min-h-screen bg-slate-50">
      <Navbar />
      <BuyerApprovalBanner />
      <HeroBanner />

      <main id="catalogue" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <Breadcrumbs f={filters} onHome={clearAll} />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Product Catalogue</h2>
            <p className="text-slate-500 mt-1 text-sm" data-testid="catalogue-count">
              {loading && !items.length ? "Loading…" : `${total} designs ${search.trim() ? "matching your search" : "in stock & ready to dispatch"}`}
            </p>
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-96">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="catalogue-search-input" placeholder="Search name, design code or SKU…" value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-full h-11 bg-white" />
            </div>
            <Button variant="outline" className="rounded-full h-11 sm:hidden" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn" aria-label="Filters">
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
            <ShareCollection />
          </div>
        </div>

        <div className="space-y-4 mb-8">
          <GenderStrip value={filters.gender} onChange={(g) => set({ gender: g, category: "All", size: "All", colour: "All", brand: "All" })} counts={facets?.genders} />
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="category-filter-new-arrivals" onClick={() => set({ newOnly: !filters.newOnly })}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                filters.newOnly ? "bg-[#FFDA00] text-slate-900" : "bg-white text-slate-700 border border-[#FFDA00] hover:bg-[#FFDA00]/10"}`}>
              <Sparkles className="h-3.5 w-3.5" /> New Arrivals
              {facets?.new_count > 0 && <span className="text-xs font-normal opacity-70">{facets.new_count}</span>}
            </button>
          </div>
          <div className={`${showFilters ? "block" : "hidden"} sm:block`}>
            <CatalogFilters f={filters} set={set} facets={facets} approved={approved} onClearAll={clearAll} />
          </div>
        </div>

        {pristine && <NewArrivalsStrip items={newArrivals.products} total={newArrivals.total} onViewAll={() => set({ newOnly: true })} />}
        {(pristine || filters.group) && (
          <CategoryTiles active={filters.group} onPick={(g) => {
            set({ group: g, category: "All" });
            setTimeout(() => document.querySelector("[data-testid='catalogue-grid']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
          }} />
        )}

        {!loading && items.length === 0 ? (
          <div className="text-center py-24 text-slate-400" data-testid="empty-catalogue">
            <SlidersHorizontal className="h-10 w-10 mx-auto mb-3 opacity-40" />
            {filters.newOnly ? "No new arrivals in the last 14 days." : "No products match your filters."}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5" data-testid="catalogue-grid">
            {items.map((p, i) => <ProductCard key={p.design_key || p.id} product={p} eager={i < 4} />)}
            {loading && Array.from({ length: items.length ? 4 : 8 }).map((_, i) => (
              <div key={`sk-${i}`} className="rounded-2xl bg-white border border-slate-200 aspect-[3/4] animate-pulse" />
            ))}
          </div>
        )}

        <InfiniteSentinel onVisible={loadMore} disabled={!hasMore || loading} />
        {hasMore && !loading && (
          <div className="text-center mt-8">
            <Button variant="outline" className="rounded-full px-8" onClick={loadMore} data-testid="load-more-btn">Load more</Button>
          </div>
        )}
        {loading && items.length > 0 && (
          <div className="flex justify-center mt-6 text-slate-400" data-testid="loading-more"><Loader2 className="h-5 w-5 animate-spin" /></div>
        )}
        {!hasMore && items.length > 0 && (
          <p className="text-center text-xs text-slate-400 mt-8" data-testid="end-of-list">Showing all {total} designs</p>
        )}
      </main>
      <Footer />
    </div>
  );
}
